"use strict";
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.findRetrieves = void 0;
var ELMDependencyHelpers_1 = require("../helpers/elm/ELMDependencyHelpers");
var ELMHelpers_1 = require("../helpers/elm/ELMHelpers");
var CustomErrors_1 = require("../types/errors/CustomErrors");
/**
 * Get all data types, and codes/valuesets used in Query ELM expressions
 *
 * @param elm main ELM library with expressions to traverse
 * @param allELM list of any dependent ELM libraries included in the main ELM
 * @param expr expression to find queries under (usually numerator for gaps in care)
 * @param queryLocalId keeps track of latest id of query statements for lookup later
 * @returns query info for each ELM retrieve
 */
function findRetrieves(elm, allELM, expr, queryLocalId, valueComparisonLocalId, expressionStack, withErrors) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (expressionStack === void 0) { expressionStack = []; }
    if (withErrors === void 0) { withErrors = []; }
    var results = [];
    // Push this expression onto the stack of processed expressions
    if (expr.localId && expr.type) {
        expressionStack.push({
            libraryName: elm.library.identifier.id,
            localId: expr.localId,
            type: expr.type
        });
    }
    // Base case, get data type and code/valueset off the expression
    if (expr.type === 'Retrieve' && expr.dataType) {
        var exprRet = expr;
        // If present, strip off HL7 prefix to data type
        var dataType = exprRet.dataType.replace(/^(\{http:\/\/hl7.org\/fhir\})?/, '');
        var queryLibraryName = elm.library.identifier.id;
        // We need to detect if this query/retrieve is used as the source of another query directly. This is an indication
        // that the retrieve is filtered by two separate queries and we need to actually look at the 'outermost' query closest
        // to the numerator.
        // This looks like the expressionStack ends with the following types ['Query', 'ExpressionRef', 'Query', 'Retrieve']
        if (expressionStack.length >= 4) {
            // Grab the last 4 in the stack and see if they match the case we are looking for
            var bottomExprs_1 = expressionStack.slice(-4);
            if (bottomExprs_1[0].type === 'Query' &&
                bottomExprs_1[1].type === 'ExpressionRef' &&
                bottomExprs_1[2].type === 'Query') {
                // check if the outer query is indeed referencing the inner one in the source.
                var queryLib = allELM.find(function (lib) { return lib.library.identifier.id === bottomExprs_1[0].libraryName; });
                if (!queryLib) {
                    throw new CustomErrors_1.UnexpectedResource('Referenced query library cannot be found.');
                }
                var outerQuery = ELMHelpers_1.findClauseInLibrary(queryLib, bottomExprs_1[0].localId);
                if (outerQuery.source[0].expression.localId === bottomExprs_1[1].localId &&
                    outerQuery.source[0].expression.type === 'ExpressionRef') {
                    // Change the queryLocalId to the outer query.
                    queryLocalId = bottomExprs_1[0].localId;
                    queryLibraryName = bottomExprs_1[0].libraryName;
                }
                else {
                    withErrors.push({
                        message: 'Query is referenced in another query but not as a single source. Gaps output may be incomplete.'
                    });
                }
            }
        }
        if (((_a = exprRet.codes) === null || _a === void 0 ? void 0 : _a.type) === 'ValueSetRef') {
            var codes = exprRet.codes;
            var valueSet = ELMDependencyHelpers_1.findValueSetReference(elm, allELM, codes);
            if (valueSet) {
                results.push({
                    dataType: dataType,
                    valueSet: valueSet.id,
                    queryLocalId: queryLocalId,
                    valueComparisonLocalId: valueComparisonLocalId,
                    retrieveLocalId: exprRet.localId,
                    retrieveLibraryName: elm.library.identifier.id,
                    queryLibraryName: queryLibraryName,
                    expressionStack: __spreadArrays(expressionStack),
                    path: exprRet.codeProperty
                });
                withErrors.push.apply(withErrors, withErrors);
            }
        }
        else if (((_b = exprRet.codes) === null || _b === void 0 ? void 0 : _b.type) === 'CodeRef' ||
            (((_c = exprRet.codes) === null || _c === void 0 ? void 0 : _c.type) === 'ToList' && ((_d = exprRet.codes.operand) === null || _d === void 0 ? void 0 : _d.type) === 'CodeRef')) {
            // ToList promotions have the CodeRef on the operand
            var codeName_1 = exprRet.codes.type === 'CodeRef'
                ? exprRet.codes.name
                : exprRet.codes.operand.name;
            var code_1 = (_e = elm.library.codes) === null || _e === void 0 ? void 0 : _e.def.find(function (c) { return c.name === codeName_1; });
            if (code_1) {
                var cs = (_f = elm.library.codeSystems) === null || _f === void 0 ? void 0 : _f.def.find(function (cs) { return cs.name == code_1.codeSystem.name; });
                results.push({
                    dataType: dataType,
                    code: {
                        system: (cs === null || cs === void 0 ? void 0 : cs.id) || code_1.codeSystem.name,
                        version: cs === null || cs === void 0 ? void 0 : cs.version,
                        display: code_1.display,
                        code: code_1.id
                    },
                    queryLocalId: queryLocalId,
                    valueComparisonLocalId: valueComparisonLocalId,
                    retrieveLocalId: exprRet.localId,
                    retrieveLibraryName: elm.library.identifier.id,
                    queryLibraryName: queryLibraryName,
                    expressionStack: __spreadArrays(expressionStack),
                    path: exprRet.codeProperty
                });
                withErrors.push.apply(withErrors, withErrors);
            }
        }
    }
    else if (expr.type === 'Query') {
        // Queries have the source array containing the expressions
        (_g = expr.source) === null || _g === void 0 ? void 0 : _g.forEach(function (s) {
            var retrieves = findRetrieves(elm, allELM, s.expression, expr.localId, valueComparisonLocalId, __spreadArrays(expressionStack), __spreadArrays(withErrors));
            results.push.apply(results, retrieves.results);
            withErrors.push.apply(withErrors, retrieves.withErrors);
        });
    }
    else if (expr.type === 'ExpressionRef') {
        // Find expression in dependent library
        if (expr.libraryName) {
            var matchingLib = ELMDependencyHelpers_1.findLibraryReference(elm, allELM, expr.libraryName || '');
            var exprRef = matchingLib === null || matchingLib === void 0 ? void 0 : matchingLib.library.statements.def.find(function (e) { return e.name === expr.name; });
            if (matchingLib && exprRef) {
                var retrieves = findRetrieves(matchingLib, allELM, exprRef.expression, queryLocalId, valueComparisonLocalId, __spreadArrays(expressionStack), __spreadArrays(withErrors));
                results.push.apply(results, retrieves.results);
                withErrors.push.apply(withErrors, retrieves.withErrors);
            }
        }
        else {
            // Find expression in current library
            var exprRef = elm.library.statements.def.find(function (d) { return d.name === expr.name; });
            if (exprRef) {
                var retrieves = findRetrieves(elm, allELM, exprRef.expression, queryLocalId, valueComparisonLocalId, __spreadArrays(expressionStack), __spreadArrays(withErrors));
                results.push.apply(results, retrieves.results);
                withErrors.push.apply(withErrors, retrieves.withErrors);
            }
        }
    }
    else if (expr.operand) {
        // Operand can be array or object. Recurse on either
        var anyExpr = expr;
        if (Array.isArray(anyExpr.operand)) {
            // Should expand to types beyond greater
            var newValueComparisonLocalId_1 = expr.type === 'Greater' ? expr.localId : valueComparisonLocalId;
            anyExpr.operand.forEach(function (e) {
                var retrieves = findRetrieves(elm, allELM, e, queryLocalId, newValueComparisonLocalId_1, __spreadArrays(expressionStack), __spreadArrays(withErrors));
                results.push.apply(results, retrieves.results);
                withErrors.push.apply(withErrors, retrieves.withErrors);
            });
        }
        else {
            var retrieves = findRetrieves(elm, allELM, anyExpr.operand, queryLocalId, valueComparisonLocalId, __spreadArrays(expressionStack), __spreadArrays(withErrors));
            results.push.apply(results, retrieves.results);
            withErrors.push.apply(withErrors, retrieves.withErrors);
        }
        // Pass through the source expression if no other cases have been satisfied
    }
    else if (expr.source) {
        var retrieves = findRetrieves(elm, allELM, expr.source, queryLocalId, valueComparisonLocalId, __spreadArrays(expressionStack), __spreadArrays(withErrors));
        results.push.apply(results, retrieves.results);
        withErrors.push.apply(withErrors, retrieves.withErrors);
    }
    return { results: results, withErrors: withErrors };
}
exports.findRetrieves = findRetrieves;
//# sourceMappingURL=RetrievesFinder.js.map